/**
 * Farm Recommendation Agent
 * Provides intelligent farm recommendations based on user preferences and location
 */

interface Farm {
  id: string;
  name: string;
  location: {
    lat: number;
    lng: number;
    address: string;
  };
  categories: string[];
  products: string[];
  rating?: number;
  distance?: number;
  certifications?: string[];
  specialty?: string[];
}

interface UserPreferences {
  location?: {
    lat: number;
    lng: number;
  };
  preferredCategories?: string[];
  preferredProducts?: string[];
  maxDistance?: number;
  organicOnly?: boolean;
  minRating?: number;
}

interface RecommendationResult {
  farm: Farm;
  score: number;
  reasons: string[];
}

class FarmRecommendationAgent {
  /**
   * Calculate distance between two coordinates using Haversine formula
   */
  private calculateDistance(
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number {
    const R = 3959; // Radius of Earth in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) *
        Math.cos(this.toRad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  private toRad(degrees: number): number {
    return degrees * (Math.PI / 180);
  }

  /**
   * Score a farm based on user preferences
   */
  private scoreFarm(farm: Farm, preferences: UserPreferences): RecommendationResult {
    let score = 0;
    const reasons: string[] = [];

    // Distance scoring (30 points max)
    if (preferences.location && farm.location) {
      const distance = this.calculateDistance(
        preferences.location.lat,
        preferences.location.lng,
        farm.location.lat,
        farm.location.lng
      );

      farm.distance = distance;

      if (distance <= 5) {
        score += 30;
        reasons.push('Very close to your location');
      } else if (distance <= 15) {
        score += 20;
        reasons.push('Nearby location');
      } else if (distance <= 30) {
        score += 10;
        reasons.push('Moderate distance');
      }
    }

    // Category matching (25 points max)
    if (preferences.preferredCategories && preferences.preferredCategories.length > 0) {
      const categoryMatches = farm.categories.filter((cat) =>
        preferences.preferredCategories?.includes(cat)
      );

      if (categoryMatches.length > 0) {
        score += Math.min(25, categoryMatches.length * 10);
        reasons.push(`Offers ${categoryMatches.join(', ')}`);
      }
    }

    // Product matching (25 points max)
    if (preferences.preferredProducts && preferences.preferredProducts.length > 0) {
      const productMatches = farm.products.filter((prod) =>
        preferences.preferredProducts?.some((pref) =>
          prod.toLowerCase().includes(pref.toLowerCase())
        )
      );

      if (productMatches.length > 0) {
        score += Math.min(25, productMatches.length * 8);
        reasons.push(`Has ${productMatches.slice(0, 3).join(', ')}`);
      }
    }

    // Rating scoring (15 points max)
    if (farm.rating) {
      score += farm.rating * 3;
      if (farm.rating >= 4.5) {
        reasons.push('Highly rated by customers');
      }
    }

    // Organic certification (5 points)
    if (farm.certifications?.includes('organic') || farm.certifications?.includes('USDA Organic')) {
      score += 5;
      if (preferences.organicOnly) {
        score += 10;
        reasons.push('Certified organic');
      }
    }

    return {
      farm,
      score,
      reasons,
    };
  }

  /**
   * Get farm recommendations based on user preferences
   */
  async getRecommendations(
    farms: Farm[],
    preferences: UserPreferences,
    limit: number = 10
  ): Promise<RecommendationResult[]> {
    // Filter farms based on hard requirements
    let filteredFarms = farms;

    if (preferences.organicOnly) {
      filteredFarms = filteredFarms.filter(
        (farm) =>
          farm.certifications?.includes('organic') ||
          farm.certifications?.includes('USDA Organic')
      );
    }

    if (preferences.minRating) {
      filteredFarms = filteredFarms.filter(
        (farm) => farm.rating && farm.rating >= (preferences.minRating || 0)
      );
    }

    if (preferences.maxDistance && preferences.location) {
      filteredFarms = filteredFarms.filter((farm) => {
        const distance = this.calculateDistance(
          preferences.location!.lat,
          preferences.location!.lng,
          farm.location.lat,
          farm.location.lng
        );
        return distance <= (preferences.maxDistance || Infinity);
      });
    }

    // Score and sort farms
    const scoredFarms = filteredFarms.map((farm) => this.scoreFarm(farm, preferences));

    scoredFarms.sort((a, b) => b.score - a.score);

    return scoredFarms.slice(0, limit);
  }

  /**
   * Get similar farms based on a reference farm
   */
  async getSimilarFarms(
    referenceFarm: Farm,
    allFarms: Farm[],
    limit: number = 5
  ): Promise<Farm[]> {
    const preferences: UserPreferences = {
      location: referenceFarm.location,
      preferredCategories: referenceFarm.categories,
      preferredProducts: referenceFarm.products,
      maxDistance: 30,
    };

    const recommendations = await this.getRecommendations(
      allFarms.filter((f) => f.id !== referenceFarm.id),
      preferences,
      limit
    );

    return recommendations.map((r) => r.farm);
  }

  /**
   * Get trending farms based on recent activity
   */
  async getTrendingFarms(farms: Farm[], limit: number = 10): Promise<Farm[]> {
    // Sort by rating and return top farms
    const sortedFarms = [...farms].sort((a, b) => {
      const ratingA = a.rating || 0;
      const ratingB = b.rating || 0;
      return ratingB - ratingA;
    });

    return sortedFarms.slice(0, limit);
  }

  /**
   * Get farms by category
   */
  async getFarmsByCategory(
    farms: Farm[],
    category: string,
    userLocation?: { lat: number; lng: number }
  ): Promise<Farm[]> {
    const filteredFarms = farms.filter((farm) =>
      farm.categories.some((cat) => cat.toLowerCase() === category.toLowerCase())
    );

    if (userLocation) {
      // Sort by distance
      filteredFarms.forEach((farm) => {
        farm.distance = this.calculateDistance(
          userLocation.lat,
          userLocation.lng,
          farm.location.lat,
          farm.location.lng
        );
      });

      filteredFarms.sort((a, b) => (a.distance || 0) - (b.distance || 0));
    }

    return filteredFarms;
  }

  /**
   * Search farms by product availability
   */
  async searchByProduct(farms: Farm[], productName: string): Promise<Farm[]> {
    return farms.filter((farm) =>
      farm.products.some((product) =>
        product.toLowerCase().includes(productName.toLowerCase())
      )
    );
  }

  /**
   * Get farms that sell a specific product
   * 🌟 CRITICAL for "Basil Example" - Produce → Farms bidirectional linking
   * @param productName - The product to search for (e.g., "basil")
   * @param limit - Maximum number of farms to return
   * @returns Array of farms that sell the product
   */
  getFarmsByProduct(productName: string, limit?: number): Farm[] {
    // Import listings data - in a real implementation this would come from a data layer
    // For now, we'll use a mock implementation that can be replaced
    const productLower = productName.toLowerCase();

    // This would normally fetch from the listings data
    // For now, return empty array - will be implemented when integrated with listings
    const allFarms: Farm[] = [];

    const filtered = allFarms.filter((farm) => {
      if (!farm.products || farm.products.length === 0) {
        return false;
      }
      return farm.products.some((product) =>
        product.toLowerCase().includes(productLower) ||
        productLower.includes(product.toLowerCase())
      );
    });

    // Sort by relevance (exact matches first, then partial matches)
    const sorted = filtered.sort((a, b) => {
      const aExact = a.products.some(p => p.toLowerCase() === productLower);
      const bExact = b.products.some(p => p.toLowerCase() === productLower);
      if (aExact && !bExact) return -1;
      if (!aExact && bExact) return 1;
      return 0;
    });

    return limit ? sorted.slice(0, limit) : sorted;
  }

  /**
   * Get farms that sell multiple products
   * 🌟 CRITICAL for Recipe → Farms bidirectional linking
   * @param products - Array of product names to search for
   * @param matchAll - If true, farm must have ALL products; if false, any product
   * @param limit - Maximum number of farms to return
   * @returns Array of farms that sell the products
   */
  getFarmsByProducts(
    products: string[],
    matchAll: boolean = false,
    limit?: number
  ): Farm[] {
    if (!products || products.length === 0) {
      return [];
    }

    const productsLower = products.map((p) => p.toLowerCase());
    const allFarms: Farm[] = []; // This would come from data layer

    const scored = allFarms.map((farm) => {
      if (!farm.products || farm.products.length === 0) {
        return { farm, matchCount: 0, matchPercentage: 0 };
      }

      const farmProductsLower = farm.products.map((p) => p.toLowerCase());
      let matchCount = 0;

      productsLower.forEach((searchProduct) => {
        const hasProduct = farmProductsLower.some(
          (farmProduct) =>
            farmProduct.includes(searchProduct) ||
            searchProduct.includes(farmProduct)
        );
        if (hasProduct) matchCount++;
      });

      const matchPercentage = (matchCount / products.length) * 100;

      return { farm, matchCount, matchPercentage };
    });

    let filtered = scored;

    if (matchAll) {
      // Farm must have ALL products
      filtered = scored.filter((item) => item.matchCount === products.length);
    } else {
      // Farm must have at least ONE product
      filtered = scored.filter((item) => item.matchCount > 0);
    }

    // Sort by match percentage (highest first), then by match count
    const sorted = filtered
      .sort((a, b) => {
        if (b.matchPercentage !== a.matchPercentage) {
          return b.matchPercentage - a.matchPercentage;
        }
        return b.matchCount - a.matchCount;
      })
      .map((item) => item.farm);

    return limit ? sorted.slice(0, limit) : sorted;
  }

  /**
   * Get farms from listings data
   * Helper method to integrate with actual listings data
   * @param productName - Optional product filter
   * @returns Array of farms from listings
   */
  private getFarmsFromListings(productName?: string): Farm[] {
    // This will be implemented when we integrate with the listings data
    // For now, return empty array
    // In real implementation:
    // import { getListingsByType } from '../../data';
    // const farms = getListingsByType('farm');
    // Convert to Farm interface and return
    return [];
  }
}

export default new FarmRecommendationAgent();
